import { useMemo, useState } from 'react'
import { Link, NavLink, useNavigate, useParams } from 'react-router-dom'
import {
  BarChart3,
  CheckSquare,
  Home,
  LineChart,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  RefreshCw,
  Search,
  Settings,
  Sparkles,
  Users,
  X
} from 'lucide-react'
import { LiverFormModal } from './LiverFormModal'
import { supabase } from '../lib/supabase'
import { useSession } from '../lib/useSession'
import { useMyManager } from '../lib/useManagerProfile'
import { useCreateLiver, useLivers } from '../lib/useLivers'
import { sidebarDesktopWidthClass, sidebarStatusFilterLabel } from '../lib/responsiveUi'
import type { LiverFormValues } from '../lib/liverForm'

type LiverStatusFilter = 'all' | 'active' | 'inactive' | 'graduated'

/**
 * Global left sidebar — liver list + bottom-row user controls.
 * Reused across all logged-in pages.
 */
export function Sidebar() {
  const session = useSession()
  const me = useMyManager()
  const livers = useLivers()
  const createLiver = useCreateLiver()
  const navigate = useNavigate()
  const { liverId } = useParams<{ liverId?: string }>()
  const [searchText, setSearchText] = useState('')
  const [statusFilter, setStatusFilter] = useState<LiverStatusFilter>('all')
  const [createOpen, setCreateOpen] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const filteredLivers = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase()
    return (livers.data ?? []).filter((liver) => {
      if (statusFilter !== 'all' && liver.status !== statusFilter) return false
      if (!normalizedSearch) return true
      return (
        liver.display_name.toLowerCase().includes(normalizedSearch) ||
        (liver.line_user_id ?? '').toLowerCase().includes(normalizedSearch)
      )
    })
  }, [livers.data, searchText, statusFilter])
  const hasLiverFilters = searchText.trim() !== '' || statusFilter !== 'all'

  async function handleCreateLiver(values: LiverFormValues) {
    const created = await createLiver.mutateAsync(values)
    setCreateOpen(false)
    setMobileOpen(false)
    navigate(`/liver/${created.id}`)
  }

  const labelClass = collapsed ? 'md:hidden' : ''
  const iconOnlyClass = collapsed ? 'md:justify-center md:px-2' : ''

  return (
    <>
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        className="fixed left-3 top-3 z-40 rounded-lg border border-gray-200 bg-white p-2 text-gray-600 shadow-sm md:hidden"
        aria-label="メニューを開く"
      >
        <Menu className="h-5 w-5" />
      </button>

      {mobileOpen && (
        <button
          type="button"
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 z-40 bg-slate-900/30 md:hidden"
          aria-label="メニューを閉じる"
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-72 flex-col border-r border-gray-200 bg-white transition-transform duration-200 md:sticky md:top-0 md:z-auto md:h-screen md:translate-x-0 md:shrink-0 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        } ${sidebarDesktopWidthClass(collapsed)}`}
      >
        <header className={`flex items-center gap-3 border-b border-gray-200 p-4 ${collapsed ? 'md:px-3' : ''}`}>
          <img src="/icon.png" alt="LogMane" className="h-9 w-9 shrink-0" />
          <div className={`min-w-0 ${labelClass}`}>
            <h1 className="text-base font-bold leading-tight text-gray-800">LogMane</h1>
            <p className="text-[10px] leading-tight text-gray-500">ログマネ</p>
          </div>
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="ml-auto rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 md:hidden"
            aria-label="メニューを閉じる"
          >
            <X className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setCollapsed((value) => !value)}
            className="ml-auto hidden rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 md:inline-flex"
            aria-label={collapsed ? 'サイドバーを展開' : 'サイドバーを折りたたむ'}
            title={collapsed ? 'サイドバーを展開' : 'サイドバーを折りたたむ'}
          >
            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </button>
        </header>

        <nav className="flex-1 space-y-1 overflow-y-auto p-2">
          <div className={`px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400 ${labelClass}`}>
            主要
          </div>
          <NavLink
            to="/"
            end
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${iconOnlyClass} ${
                isActive ? 'bg-slate-100 font-medium text-slate-900' : 'text-gray-700 hover:bg-gray-100'
              }`
            }
            title="ホーム"
          >
            <Home className="h-4 w-4 text-gray-400" />
            <span className={labelClass}>ホーム</span>
          </NavLink>
          <NavLink
            to="/tasks"
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${iconOnlyClass} ${
                isActive ? 'bg-slate-100 font-medium text-slate-900' : 'text-gray-700 hover:bg-gray-100'
              }`
            }
            title="TODO"
          >
            <CheckSquare className="h-4 w-4 text-gray-400" />
            <span className={labelClass}>TODO</span>
          </NavLink>
          <NavLink
            to="/reports"
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${iconOnlyClass} ${
                isActive ? 'bg-slate-100 font-medium text-slate-900' : 'text-gray-700 hover:bg-gray-100'
              }`
            }
            title="レポート"
          >
            <BarChart3 className="h-4 w-4 text-gray-400" />
            <span className={labelClass}>レポート</span>
          </NavLink>
          <NavLink
            to="/feedback"
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${iconOnlyClass} ${
                isActive ? 'bg-slate-100 font-medium text-slate-900' : 'text-gray-700 hover:bg-gray-100'
              }`
            }
            title="フィードバック"
          >
            <Sparkles className="h-4 w-4 text-gray-400" />
            <span className={labelClass}>フィードバック</span>
          </NavLink>
          <NavLink
            to="/line"
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${iconOnlyClass} ${
                isActive ? 'bg-slate-100 font-medium text-slate-900' : 'text-gray-700 hover:bg-gray-100'
              }`
            }
            title="LINE"
          >
            <LineChart className="h-4 w-4 text-gray-400" />
            <span className={labelClass}>LINE</span>
          </NavLink>
          <NavLink
            to="/livers"
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${iconOnlyClass} ${
                isActive ? 'bg-slate-100 font-medium text-slate-900' : 'text-gray-700 hover:bg-gray-100'
              }`
            }
            title="ライバー管理"
          >
            <Users className="h-4 w-4 text-gray-400" />
            <span className={labelClass}>ライバー管理</span>
          </NavLink>
          <NavLink
            to="/handoffs"
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${iconOnlyClass} ${
                isActive ? 'bg-slate-100 font-medium text-slate-900' : 'text-gray-700 hover:bg-gray-100'
              }`
            }
            title="引き継ぎ"
          >
            <RefreshCw className="h-4 w-4 text-gray-400" />
            <span className={labelClass}>引き継ぎ</span>
          </NavLink>

          <div className="flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-wide text-gray-400">
            <Users className="h-3 w-3" />
            <span className={labelClass}>ライバー</span>
            <span className={`ml-auto normal-case tracking-normal text-gray-300 ${labelClass}`}>
              {livers.data ? `${filteredLivers.length}/${livers.data.length}` : ''}
            </span>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-sky-600"
              title="ライバーを追加"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className={`space-y-2 px-2 pb-2 ${labelClass}`}>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder="名前 / LINE ID"
                aria-label="ライバー検索"
                className="w-full rounded-md border border-gray-200 bg-white py-1.5 pl-8 pr-2 text-xs text-gray-700 outline-none transition placeholder:text-gray-400 focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as LiverStatusFilter)}
              aria-label="statusフィルタ"
              className="w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700 outline-none transition focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
            >
              {(['all', 'active', 'inactive', 'graduated'] as const).map((status) => (
                <option key={status} value={status}>{sidebarStatusFilterLabel(status)}</option>
              ))}
            </select>
          </div>
          {livers.isLoading ? (
            <div className="px-3 py-2 text-sm text-gray-400">読み込み中…</div>
          ) : livers.error ? (
            <div className="px-3 py-2 text-sm text-red-500">{String(livers.error)}</div>
          ) : filteredLivers.length === 0 && hasLiverFilters ? (
            <div className="px-3 py-2 text-sm text-gray-400">該当するライバーはいません</div>
          ) : (
            filteredLivers.map((liver) => {
              const isSelected = liver.id === liverId
              return (
                <NavLink
                  key={liver.id}
                  to={`/liver/${liver.id}`}
                  onClick={() => setMobileOpen(false)}
                  title={liver.display_name}
                  className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors ${iconOnlyClass} ${
                    isSelected
                      ? 'bg-sky-50 font-medium text-sky-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <span className={labelClass}>{liver.display_name}</span>
                  <span className={`hidden ${collapsed ? 'md:inline' : ''}`}>
                    {liver.display_name.slice(0, 1)}
                  </span>
                </NavLink>
              )
            })
          )}
        </nav>

        <footer className="flex items-center justify-between border-t border-gray-200 p-3 text-xs text-gray-500">
          <div className={`min-w-0 truncate ${labelClass}`}>
            <div className="truncate font-medium text-gray-700">
              {me.data?.name ?? session?.user.email ?? ''}
            </div>
            {me.data?.name && (
              <div className="truncate text-[10px] text-gray-400">{session?.user.email}</div>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Link
              to="/settings"
              className="rounded p-1.5 text-gray-500 hover:bg-gray-100"
              title="設定"
              onClick={() => setMobileOpen(false)}
            >
              <Settings className="h-4 w-4" />
            </Link>
            <button
              type="button"
              onClick={() => {
                void supabase.auth.signOut()
              }}
              className="rounded p-1.5 text-gray-500 hover:bg-gray-100"
              title="ログアウト"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </footer>

        <LiverFormModal
          open={createOpen}
          mode="create"
          loading={createLiver.isPending}
          error={createLiver.error instanceof Error ? createLiver.error.message : null}
          onClose={() => setCreateOpen(false)}
          onSubmit={handleCreateLiver}
        />
      </aside>
    </>
  )
}
